import { FC } from 'react';
import { useTypedDispatch, useTypedSelector } from '@/store/hooks';
import {
	ActionIcon,
	Box,
	Divider,
	Drawer,
	Flex,
	Stack,
	Text,
	Title,
} from '@mantine/core';
import { creatorActions } from '@/store/slices/creatorSlice';
import { formatPrice } from '@/utils/utilities';
import { groupBy } from 'lodash';
import { DisplayAttributeValueName } from '@/components/ui/DisplayAttributeValueName';
import { useContent } from '@/providers/ContentProvider';
import ArrowLeftIcon from '@/assets/icons/arrowLeft.svg?react';

const baseSlug = 'creatorSummaryDrawer.';

export const CreatorSummaryDrawer: FC = () => {
	const dispatch = useTypedDispatch();

	const { getContent } = useContent();

	const { options, project, preset } = useTypedSelector(
		(state) => state.creator
	);

	const handleClose = () => {
		dispatch(creatorActions.updateOptions({ summaryDrawerOpened: false }));
	};

	const modules = options.isPresetMode
		? preset.creatorProductModules
		: project.creatorProductModules;

	return (
		<Drawer
			opened={options.summaryDrawerOpened}
			onClose={handleClose}
			title={
				<Flex gap="sm" align="center">
					<ActionIcon onClick={handleClose} variant="transparent">
						<ArrowLeftIcon />
					</ActionIcon>
					<Text size="lg" fw="bold">
						{getContent(`${baseSlug}title`)}
					</Text>
				</Flex>
			}
		>
			<Box pt="sm">
				{Object.values(groupBy(modules, 'code')).map((creaProdModules) => {
					const creaProdModule = creaProdModules[0],
						price = creaProdModule.price / 100;

					return (
						<Box key={creaProdModule.id}>
							<Title order={4} mb="md">
								{creaProdModule.code}
							</Title>
							<Stack gap="xs">
								<Flex justify="space-between">
									<Text c="dimmed">{getContent(`${baseSlug}moduleLabel`)}</Text>
									<Text>
										{creaProdModule.productModule!.storeConfiguration.name}
									</Text>
								</Flex>
								{[...creaProdModule.selectedAttributeValues!]
									.sort((a, b) => {
										const order = [
											'Tkanina',
											'Kolor',
											'Wybarwienie nóżek',
											'Sposób wykończenia',
										];
										const aIndex = order.indexOf(a.attribute.name);
										const bIndex = order.indexOf(b.attribute.name);
										if (aIndex === -1) return 1;
										if (bIndex === -1) return -1;
										return aIndex - bIndex;
									})
									.map((selectedAttributeValue) => (
										<Flex
											key={selectedAttributeValue.id}
											justify="space-between"
										>
											<Text c="dimmed">
												{selectedAttributeValue.attribute.name}:
											</Text>
											<Text>
												<DisplayAttributeValueName
													attributeValue={selectedAttributeValue}
												/>{' '}
											</Text>
										</Flex>
									))}
								<Flex justify="space-between">
									<Text c="dimmed">
										{getContent(`${baseSlug}pricePerUnitLabel`)}
									</Text>
									<Text>{formatPrice(price)}</Text>
								</Flex>
								<Flex justify="space-between">
									<Text c="dimmed">
										{getContent(`${baseSlug}quantityLabel`)}
									</Text>
									<Text c="dimmed">
										{getContent(`${baseSlug}quantityLabel`, {
											vars: {
												$quantity: creaProdModules.length,
											},
										})}
									</Text>
								</Flex>
								<Flex justify="space-between">
									<Text c="dimmed">{getContent(`${baseSlug}totalLabel`)}</Text>
									<Text>{formatPrice(creaProdModules.length * price)}</Text>
								</Flex>
							</Stack>
							<Divider my="md" />
						</Box>
					);
				})}
				<Flex justify="space-between">
					<Text fw="bold">{getContent(`${baseSlug}totalLabel`)}</Text>
					<Text fw="bold">{formatPrice(project.price / 100)}</Text>
				</Flex>
			</Box>
		</Drawer>
	);
};
